/**
 * Primitive GitHub operations, each authored as a Macrokit *utility* macro
 * (category: "utility"). These are the low-level tools the strong model
 * (Claude) orchestrates when it executes a novel task. The runtime records
 * the trajectory of primitive calls; the distiller generalizes that
 * trajectory into a single parameterized *domain* macro.
 *
 * Read-only, except `gh_suggest_labels_dryrun`, which is a DRY-RUN write:
 * it never mutates the repo, it only reports what it *would* apply. A
 * trajectory that touches it produces a "candidate" macro, not an
 * auto-trusted one (honesty over magic).
 *
 * Every handler pulls the shared GitHubClient from ctx.tools.github — the
 * standard Macrokit injection pattern, so the same primitives are testable
 * against a fake client.
 */
import { defineMacro, type AuthoredMacro } from "@macrokit/authoring";
import { MacroRegistry } from "@macrokit/runtime";
import { z } from "zod";
import type { GitHubClient } from "./github.js";

function gh(ctx: { tools: Record<string, unknown> }): GitHubClient {
  const client = ctx.tools.github as GitHubClient | undefined;
  if (!client) throw new Error("primitive requires ctx.tools.github (GitHubClient)");
  return client;
}

export const ghGetRepo = defineMacro({
  name: "gh_get_repo",
  intent: "Fetch basic metadata for a public GitHub repository (stars, open-issue count, default branch).",
  category: "utility",
  schema: z.object({ owner: z.string(), repo: z.string() }),
  handler: async ({ owner, repo }, ctx) => gh(ctx).getRepo(owner, repo),
});

export const ghListPulls = defineMacro({
  name: "gh_list_pulls",
  intent: "List pull requests for a public repo, newest first. Returns number, title, author, createdAt.",
  category: "utility",
  schema: z.object({
    owner: z.string(),
    repo: z.string(),
    state: z.enum(["open", "closed", "all"]).default("open"),
  }),
  handler: async ({ owner, repo, state }, ctx) => gh(ctx).listPulls(owner, repo, state),
});

export const ghGetPull = defineMacro({
  name: "gh_get_pull",
  intent: "Fetch a single pull request's details: body, additions/deletions, changed file count, base/head refs.",
  category: "utility",
  schema: z.object({ owner: z.string(), repo: z.string(), number: z.number().int() }),
  handler: async ({ owner, repo, number }, ctx) => gh(ctx).getPull(owner, repo, number),
});

export const ghListPullFiles = defineMacro({
  name: "gh_list_pull_files",
  intent: "List the files changed in a pull request, with per-file status and line counts.",
  category: "utility",
  schema: z.object({ owner: z.string(), repo: z.string(), number: z.number().int() }),
  handler: async ({ owner, repo, number }, ctx) => gh(ctx).listPullFiles(owner, repo, number),
});

export const ghListIssues = defineMacro({
  name: "gh_list_issues",
  intent: "List issues for a public repo (excludes pull requests). Returns number, title, labels, comment count.",
  category: "utility",
  schema: z.object({
    owner: z.string(),
    repo: z.string(),
    state: z.enum(["open", "closed", "all"]).default("open"),
    labels: z.string().optional(),
  }),
  handler: async ({ owner, repo, state, labels }, ctx) =>
    gh(ctx).listIssues(owner, repo, state, labels),
});

export const ghSuggestLabelsDryRun = defineMacro({
  name: "gh_suggest_labels_dryrun",
  intent:
    "DRY-RUN ONLY. Report which labels WOULD be applied to an issue or PR. Never mutates the repo. " +
    "Use this to propose triage labels; the demo never performs real writes.",
  category: "utility",
  schema: z.object({
    owner: z.string(),
    repo: z.string(),
    number: z.number().int(),
    labels: z.array(z.string()),
  }),
  handler: async ({ owner, repo, number, labels }) => ({
    dryRun: true as const,
    target: `${owner}/${repo}#${number}`,
    wouldApply: labels,
    note: "dry-run — no write performed",
  }),
});

/** Every primitive, in a stable order. (`any` generics: heterogeneous macros.) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PRIMITIVES: ReadonlyArray<AuthoredMacro<any, any>> = [
  ghGetRepo,
  ghListPulls,
  ghGetPull,
  ghListPullFiles,
  ghListIssues,
  ghSuggestLabelsDryRun,
];

/** Names of primitives that perform a (dry-run) write — used for trust classification. */
export const WRITE_PRIMITIVES = new Set<string>(["gh_suggest_labels_dryrun"]);

/** Build a fresh registry containing only the primitive (utility) macros. */
export function buildPrimitivesRegistry(): MacroRegistry {
  const reg = new MacroRegistry();
  for (const p of PRIMITIVES) reg.register(p);
  return reg;
}
